import {Box, Stack, Typography} from "@mui/material";
import React, {useEffect, useRef, useState} from "react";
import Chart from "react-apexcharts";
import {blue, green, grey, purple} from "@mui/material/colors";
import {useDashboard} from "../../../../context/DashboardContext";
import moment from "moment";
import DateRangePickerWithMenu from "../../../../components/date-range/DateRangePickerWithMenu";

export const TagsUsageChart = ({project}) => {
    const {
        tagUsage,
        tagsUsageLoading,
        refreshTagsUsage,
        dateRange: globalDateRange
    } = useDashboard();

    const [startDate, setStartDate] = useState(() => globalDateRange?.start || moment().startOf("day"));
    const [endDate, setEndDate] = useState(() => globalDateRange?.end || moment().endOf("day"));
    const [rangeLabel, setRangeLabel] = useState(() => globalDateRange?.label || "Today");

    // Feed ApexCharts an explicit width so it always redraws at the card's real
    // size (it otherwise caches a stale width across zoom reflows).
    const chartBoxRef = useRef(null);
    const [chartWidth, setChartWidth] = useState(0);

    useEffect(() => {
        const el = chartBoxRef.current;
        if (!el || typeof ResizeObserver === "undefined") return;
        const ro = new ResizeObserver((entries) => {
            const width = Math.round(entries[0].contentRect.width);
            setChartWidth((prev) => (prev === width ? prev : width));
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        setStartDate(globalDateRange?.start || null);
        setEndDate(globalDateRange?.end || null);
        setRangeLabel(globalDateRange?.label || "Today");
    }, [globalDateRange]);

    useEffect(() => {
        if (project?.id && startDate && endDate) {
            const isUsingGlobalRange =
                startDate?.valueOf() === globalDateRange?.start?.valueOf() &&
                endDate?.valueOf() === globalDateRange?.end?.valueOf();

            if (!isUsingGlobalRange) {
                refreshTagsUsage(startDate, endDate);
            }
        }
    }, [
        project?.id,
        startDate,
        endDate,
        globalDateRange,
        refreshTagsUsage
    ]);

    // Process chart data
    const chartLabels = [];
    const chartValues = [];

    for (const key of Object.keys(tagUsage).sort()) {
        if (tagUsage[key]) {
            chartLabels.push(key);
            chartValues.push(tagUsage[key]);
        }
    }

    const hasData = chartLabels.length > 0;

    const mainMenuCount = tagUsage?.main_menu || 0;
    const appointmentCount = tagUsage?.create_appointment || 0;
    const conversionRate = mainMenuCount > 0 ? (appointmentCount / mainMenuCount) * 100 : 0;

    const chartOptions = {
        chart: {type: 'bar', height: '100%', toolbar: {show: false}},
        colors: [blue[400]],
        plotOptions: {
            bar: {
                borderRadius: 4,
                horizontal: false,
                columnWidth: "50%",
                dataLabels: {
                    position: "top",
                },
            }
        },
        dataLabels: {
            enabled: true,
            offsetY: -22,
            style: {
                colors: [grey[900]],
                fontWeight: 600,
                fontSize: "12px"
            },
        },
        xaxis: {
            categories: chartLabels,
            labels: {
                rotate: 0,
                style: {colors: grey[500], fontSize: "11px"},
                trim: true,
                hideOverlappingLabels: true,
            },
            axisBorder: {show: false},
            axisTicks: {show: false},
        },
        yaxis: {
            labels: {
                style: {colors: grey[400], fontSize: "11px"}
            }
        },
        grid: {
            borderColor: grey[200],
            strokeDashArray: 3,
            padding: {top: 20},
        },
    };

    const chartSeries = [{name: 'Usage', data: chartValues}];

    // Cap the plot width by the number of tags so a few bars don't stretch
    // apart across a wide card. ~96px per bar keeps a consistent thickness.
    const maxChartWidth = Math.max(chartLabels.length * 96, 240);

    return (
        <Stack direction="column" gap={0}
               justifyContent={"space-between"}
               sx={{borderRadius: 3, flex: 1, minHeight: 0, p: 0}}
               className={"border-shadow"}>
            <Stack direction="row" alignItems="center" justifyContent="space-between"
                   sx={{px: 2, pt: 2, pb: 1, gap: 1}}>
                <Stack direction="row" alignItems="center" gap={1.5}>
                    <Typography variant={"body1"} color={grey[500]}>TAGS USAGE</Typography>
                    {/*{mainMenuCount > 0 && (*/}
                    {/*    // <Tooltip title={"Successfully booked"}>*/}
                    {/*    //     <Box sx={{*/}
                    {/*    //         bgcolor: green[50], color: green[700],*/}
                    {/*    //         px: 1.25, py: 0.4, borderRadius: 2,*/}
                    {/*    //         fontSize: 12, fontWeight: 600*/}
                    {/*    //     }}>*/}
                    {/*    //         {conversionRate.toFixed(1)}%*/}
                    {/*    //     </Box>*/}
                    {/*    // </Tooltip>*/}
                    {/*)}*/}
                </Stack>
                <Box sx={{flexShrink: 0}}>
                    <DateRangePickerWithMenu
                        triggerSx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 1,
                            minWidth: 0,
                            bgcolor: grey[100],
                            borderRadius: 2,
                            px: 1.5,
                            py: 0.5,
                            fontSize: 13,
                            textTransform: "none",
                            color: "text.primary",
                            "&:hover": {bgcolor: grey[200]},
                        }}
                        controlledStartDate={startDate}
                        controlledEndDate={endDate}
                        controlledLabel={rangeLabel}
                        onChange={({label, start, end}) => {
                            setStartDate(start);
                            setEndDate(end);
                            setRangeLabel(label);
                        }}
                    />
                </Box>
            </Stack>
            <Box sx={{flex: 1, minHeight: 0, overflowY: "auto", px: 1, pb: 1}}>
                {tagsUsageLoading ? (
                    <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", height: "100%"}}>
                        <Typography color={grey[400]}>Loading...</Typography>
                    </Box>
                ) : hasData ? (
                    <Box ref={chartBoxRef}
                         sx={{minHeight: 260, height: "100%", width: "100%", maxWidth: maxChartWidth, mx: "auto"}}>
                        <Chart options={chartOptions} series={chartSeries} type="bar"
                               width={chartWidth || "100%"} height="100%"/>
                    </Box>
                ) : (
                    <Box sx={{
                        display: "flex", alignItems: "center", justifyContent: "center",
                        height: "100%", color: grey[500], flexDirection: "column", gap: 1
                    }}>
                        <Typography variant="body1" color={grey[500]}>No tags data available</Typography>
                        <Typography variant="body2" color={grey[400]}>Try selecting a different date range</Typography>
                    </Box>
                )}
            </Box>
        </Stack>
    );
};
